import { Skeleton } from "@mui/material";
import { useDeepCompareEffect } from "@react-hookz/web";
import { useReducer, type FC } from "react";
import { useTranslation } from "react-i18next";
import { extractZodTreeErrorProps } from "../../../utils/errors/extract-zod-tree-error-props";
import type { UUID } from "../../../utils/uuid";
import { PageWithFooterWrapper } from "../../generic/components/page-with-footer-wrapper";
import { useFollowUpMomentDetails } from "../hooks/use-follow-up-moment-details";
import { followUpReducer, makeDefaultFollowUpState } from "../reducer/reducer";
import type { FollowUpChannelWithData, FollowUpSchemaType } from "../reducer/schema";
import { FollowUpBody } from "./follow-up-body/follow-up-body";
import { FollowUpBodySection } from "./follow-up-body/follow-up-body-section";
import { FollowUpChannelSelect } from "./follow-up-channel-select/follow-up-channel-select";
import { FollowUpHeader } from "./follow-up-header";
import { FollowUpMessage } from "./follow-up-message/follow-up-message";
import { FollowUpScheduleFinished } from "./follow-up-schedule/follow-up-schedule-finished";
import { FollowUpScheduleScheduled } from "./follow-up-schedule/follow-up-schedule-scheduled";
import { FollowUpSidebar } from "./follow-up-sidebar";

export const FollowUpWrapper: FC<{ moment_id: UUID }> = ({ moment_id }) => {
  const { t } = useTranslation();

  const loadState = useFollowUpMomentDetails(moment_id);

  const [state, dispatch] = useReducer(followUpReducer, makeDefaultFollowUpState());

  useDeepCompareEffect(() => {
    if (loadState.loading) dispatch({ type: "LOAD_START" });
    else if (loadState.error) dispatch({ type: "LOAD_ERROR", error: loadState.error });
    else if (loadState.data) dispatch({ type: "LOAD_SUCCESS", payload: loadState.data });
  }, [loadState]);

  return (
    <>
      <FollowUpHeader
        loading={state.momentDetails.loading}
        programTitle={state.momentDetails.data?.momentFollowUpDetails.program?.title ?? undefined}
        title={state.ui.title}
        setTitle={(title: string) => dispatch({ type: "SET_TITLE", payload: title })}
        error={extractZodTreeErrorProps(state.errors, ["title"])}
      />

      <PageWithFooterWrapper>
        <FollowUpSidebar
          followUpMomentDetailsState={state.momentDetails}
          reason={state.ui.reason}
          setReason={(reason) => dispatch({ type: "SET_REASON", payload: reason })}
        />
        <FollowUpBody>
          {state.momentDetails.loading ? (
            <Skeleton variant="rounded" width="100%" height={275} animation="wave" />
          ) : (
            <FollowUpChannelSelect
              enabledChannels={state.momentDetails.data?.enabledChannels || []}
              selectedChannel={state.ui.channel}
              setChannel={(payload) => dispatch({ type: "SET_CHANNEL", payload })}
              segmentPersons={state.momentDetails.data?.segmentPersons || []}
            />
          )}

          {state.momentDetails.loading ? (
            <Skeleton variant="rounded" width="100%" height={275} animation="wave" />
          ) : (
            <FollowUpBodySection
              title={t("Schedule")}
              subtitle={t(scheduleSubtitleMessage(state.ui.mode))}
            >
              {state.ui.mode === "finished" && (
                <FollowUpScheduleFinished
                  value={state.ui.schedule}
                  onChange={(schedule) =>
                    dispatch({ type: "SET_SCHEDULE", payload: { mode: "finished", schedule } })
                  }
                />
              )}
              {state.ui.mode === "scheduled" && (
                <FollowUpScheduleScheduled
                  value={state.ui.schedule}
                  onChange={(schedule) =>
                    dispatch({ type: "SET_SCHEDULE", payload: { mode: "scheduled", schedule } })
                  }
                />
              )}
            </FollowUpBodySection>
          )}

          {state.momentDetails.loading ? (
            <Skeleton variant="rounded" width="100%" height={400} animation="wave" />
          ) : (
            <FollowUpMessage
              channel={
                {
                  channel: state.ui.channel,
                  channelData: state.ui.channelData,
                } as FollowUpChannelWithData
              }
              setChannelData={(payload) => dispatch({ type: "SET_CHANNEL", payload })}
              mergeTags={state.momentDetails.data?.mergeTags || []}
              toTarget={state.derivedData.toTarget}
              setDesign={(payload) => dispatch({ type: "SET_MESSAGE_DESIGN", payload })}
              design={state.ui.design}
            />
          )}
        </FollowUpBody>
      </PageWithFooterWrapper>
    </>
  );
};

function scheduleSubtitleMessage(mode: FollowUpSchemaType["mode"]): string {
  switch (mode) {
    case "finished":
      return "When do you want to send the Follow-Up?";
    case "scheduled":
      return `Choose how long to wait after the recipient receives the message before sending the Follow-Up. If you change the send date of the original message later, the Follow-Up will still send after the period you define here.`;
  }
}
